/**
 * KeyFacultyCard — "Key faculty in the department" block from the
 * Feb 2026 course-page redesign follow-up.
 *
 * Schema (`degree.key_faculty[]`):
 *   [{
 *     name:            "Dr Jane Doe",
 *     role:            "Reader in Comparative Politics",
 *     research_focus:  "Electoral systems in emerging democracies.",
 *     linkedin_url:    "https://…",
 *     photo_url:       "https://…"       (optional)
 *     department_url:  "https://…"       (optional — profile on
 *                                          the department page)
 *   }, …]
 *
 * Renders nothing when the degree has zero seeded faculty entries.
 * Silent-empty by design — a fabricated professor is worse than no
 * professor. Ops seeds this manually per pilot degree; we don't
 * scrape department pages (brittle, per-uni HTML).
 */
import { UserSquare2, ExternalLink } from "lucide-react";

export default function KeyFacultyCard({ faculty }) {
  const list = (Array.isArray(faculty) ? faculty : [])
    .filter((f) => f && typeof f.name === "string" && f.name.trim().length > 0)
    .slice(0, 4);
  if (list.length === 0) return null;

  return (
    <section
      data-testid="key-faculty-card"
      className="mt-8 rounded-2xl border border-stone-200 bg-white/70 px-6 py-6 sm:px-8 sm:py-7"
    >
      <div className="flex items-center gap-2 mb-4">
        <UserSquare2
          className="h-4 w-4 text-[#4C1D95]"
          strokeWidth={2.25}
          aria-hidden
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#4C1D95]">
          Key faculty
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {list.map((f, i) => (
          <article
            key={`${f.name}-${i}`}
            data-testid={`key-faculty-item-${i}`}
            className="flex items-start gap-3 rounded-xl border border-stone-200 bg-[#FAF7F1] px-4 py-3.5"
          >
            {f.photo_url ? (
              <img
                src={f.photo_url}
                alt={f.name}
                loading="lazy"
                className="h-12 w-12 rounded-full object-cover border border-stone-200 shrink-0"
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#F5F0FF] to-white border border-[#4C1D95]/25 flex items-center justify-center shrink-0">
                <span className="font-editorial text-lg text-[#4C1D95]">
                  {f.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                </span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-editorial text-base text-stone-900 leading-tight">
                  {f.name}
                </span>
                {f.linkedin_url && (
                  <a
                    href={f.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`key-faculty-linkedin-${i}`}
                    aria-label={`${f.name} on LinkedIn`}
                    className="text-stone-400 hover:text-[#0A66C2] transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" strokeWidth={2.25} />
                  </a>
                )}
              </div>
              {f.role && (
                <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mt-0.5">
                  {f.role}
                </div>
              )}
              {f.research_focus && (
                <div className="text-[13px] text-stone-700 leading-snug mt-1.5">
                  {f.research_focus}
                </div>
              )}
              {f.department_url && (
                <a
                  href={f.department_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`key-faculty-dept-${i}`}
                  className="inline-block mt-1.5 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
                >
                  Department profile ↗
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
